<template>
  <div>
    <h3>{{ t('toplisting.modal.immocredit.title') }}</h3>
    <template v-if="data">
      <HgCollapsible
        :class="$style.collapsible"
        :triggerCssClass="$style.collapsibleTrigger"
        @expanded="collapsibleExpanded = true"
        @collapsed="collapsibleExpanded = false"
      >
        <template #header>
          <div :class="$style.collapsibleHeaderContent">
            <div :class="$style.collapsibleHeaderFirstRow">
              <span>
                <span :class="$style.creditAvailableInCHF">
                  {{
                    t('toplisting.modal.immocredit.creditAvailableInCHF', {
                      amountInCHF: formatPriceInCHFCents(data.availableCreditInCHFCents),
                    })
                  }}
                </span>
                {{ t('toplisting.modal.immocredit.creditAvailable').toLowerCase() }}
              </span>
              <HgBadge
                v-if="calculateDaysUntilExpiration <= 7"
                :text="
                  t(`toplisting.modal.immocredit.expiresInDays`, {
                    days: calculateDaysUntilExpiration,
                  })
                "
                theme="orange"
              />
            </div>
            <div :class="$style.creditToplistingDays">
              <span>
                {{ t('toplisting.modal.immocredit.creditEquivalentInToplistingDays') }}
                {{ data.maximumTopListingDays }}
              </span>
              <span :class="$style.premiumDaysText">
                {{ t('toplisting.modal.immocredit.creditEquivalentInPremiumToplistingDays') }}
                {{ data.maximumPremiumListingDays }}
              </span>
            </div>
            <HgNotificationMessage
              v-if="!collapsibleExpanded && noCreditLeftNotificationTitle"
              :class="$style.notificationContainer"
              :messageClass="$style.notificationMessage"
              theme="info"
            >
              <div :class="$style.notificationTitle">{{ noCreditLeftNotificationTitle }}</div>
            </HgNotificationMessage>
          </div>
        </template>
        <div :class="$style.collapsibleBodyContent">
          <div :class="$style.donutChart">
            <DonutChart
              :labels="donutLabels"
              :series="donutSeries"
              :gradientColorsFrom="donutGradientColorsFrom"
              :gradientColorsTo="donutGradientColorsTo"
              :title="t('toplisting.modal.immocredit.monthlyCreditBreakdown')"
              :usedCreditCHF="roundToTwoDecimalPlaces(data.usedCreditInCHFCents / 100)"
              :availableCreditCHF="roundToTwoDecimalPlaces(data.availableCreditInCHFCents / 100)"
            />
          </div>
          <div :class="$style.donutChartLegend">
            <ToplistingCreditLabel
              :iconColor="'blue'"
              :labelName="t('toplisting.modal.immocredit.creditUsed')"
              :labelHint="
                t('toplisting.modal.immocredit.creditUsedSinceDate', {
                  date: formattedPeriodStartDate,
                })
              "
              :formattedPrice="formatPriceInCHFCents(data.usedCreditInCHFCents)"
            />
            <ToplistingCreditLabel
              :iconColor="'gray'"
              :labelName="t('toplisting.modal.immocredit.creditAvailable')"
              :labelHint="
                t('toplisting.modal.immocredit.creditAvailableUntil', {
                  date: formattedPeriodEndDate,
                })
              "
              :labelExtra="[
                t('toplisting.modal.immocredit.creditAvailableExtraInfoTop', {
                  days: calculatedCreditTop,
                }),
                t('toplisting.modal.immocredit.creditAvailableExtraInfoPremium', {
                  days: calculatedCreditPremium,
                }),
              ]"
              :formattedPrice="formatPriceInCHFCents(data.availableCreditInCHFCents)"
            />
          </div>
          <HgNotificationMessage
            v-if="noCreditLeftNotificationTitle"
            theme="info"
            :class="$style.notificationContainer"
            :messageClass="$style.notificationMessage"
          >
            <div :class="$style.notificationTitle">{{ noCreditLeftNotificationTitle }}</div>
            <span>{{ noCreditLeftNotificationMessage }}</span>
            <span v-if="shouldShowNextCreditAvailableMessage">
              <br />
              {{ nextCreditAvailableMessage }}
            </span>
          </HgNotificationMessage>
          <HgNotificationMessage
            v-else-if="shouldShowNextCreditAvailableMessage"
            theme="info"
            :iconClass="$style.notificationNoIcon"
            :class="$style.notificationContainer"
            :messageClass="$style.notificationMessage"
          >
            {{ nextCreditAvailableMessage }}
          </HgNotificationMessage>
        </div>
      </HgCollapsible>
      <span :class="$style.hint">
        {{ t('toplisting.modal.immocredit.creditHint') }}
      </span>
    </template>
    <div v-if="!data" :class="$style.errorMessage">
      {{ t('toplisting.modal.immocredit.immocreditsUnavailable') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  HgBadge,
  HgCollapsible,
  HgNotificationMessage,
  HgSupportedLanguage,
  hgNumber,
} from '@smg-real-estate/domus';
import { computed, ref } from 'vue';

import { type ToplistingCreditUsage } from '@/api/toplistingService';
import DonutChart from '@/components/DonutChart/DonutChart.vue';
import ToplistingCreditLabel from '@/components/ToplistingCredit/ToplistingCreditLabel.vue';
import { calculateDaysUntilTargetDate, mapToDotFormattedDate } from '@/helpers/dateUtils';
import { formatNumberWithTwoDecimals, roundToTwoDecimals } from '@/helpers/numberUtils';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const { locale, t } = useI18n();
const route = useRoute();

const { data, toplistingEndDate } = defineProps({
  data: {
    type: Object as () => ToplistingCreditUsage,
    required: false,
    default: null,
  },
  toplistingEndDate: {
    type: String,
    required: false,
    default: null,
  },
});

const collapsibleExpanded = ref(false);
const donutLabels = [
  t('toplisting.modal.immocredit.expanded.usedImmoCredits'),
  t('toplisting.modal.immocredit.expanded.availableImmoCredits'),
];
const donutGradientColorsFrom = ['#2B3486', '#DFE4E9'];
const donutGradientColorsTo = ['#288DD0', '#AFBECA'];

const donutSeries = computed<number[]>(() => {
  return [
    roundToTwoDecimalPlaces(data.usedCreditInCHFCents / 100),
    roundToTwoDecimalPlaces(data.availableCreditInCHFCents / 100),
  ];
});

const noCreditLeftNotificationTitle = computed<string | null>(() => {
  if (data.availableCreditInCHFCents === 0) {
    return t('toplisting.modal.immocredit.folded.creditFullyUsedUpWithoutCreditLeft') as string;
  } else if (data.availableCreditInCHFCents > 0 && data.amountNotCoveredByCreditInCHFCents > 0) {
    return t('toplisting.modal.immocredit.folded.creditFullyUsedUpWithCreditLeft') as string;
  }

  return null;
});
const noCreditLeftNotificationMessage = computed<string | null>(() => {
  if (data.availableCreditInCHFCents === 0) {
    if (toplistingEndDate) {
      return t(
        'toplisting.modal.immocredit.expanded.additionalCostsInCHFWithMonthlyCreditAndNoCreditLeftAndClosedToplistingUntilDate',
        {
          amountInCHF: formatPriceInCHFCents(data.amountNotCoveredByCreditInCHFCents),
          date: mapToDotFormattedDate(data.periodEndDate),
        },
      ) as string;
    } else {
      return t(
        'toplisting.modal.immocredit.expanded.additionalCostsInCHFWithMonthlyCreditAndNoCreditLeftAndOpenEndedToplistingUntilDate',
        {
          date: mapToDotFormattedDate(data.periodEndDate),
        },
      ) as string;
    }
  } else if (data.availableCreditInCHFCents > 0 && data.amountNotCoveredByCreditInCHFCents > 0) {
    return t(
      'toplisting.modal.immocredit.expanded.additionalCostsInCHFWithMonthlyCreditAndCreditLeftUntilDate',
      {
        amountInCHF: formatPriceInCHFCents(data.amountNotCoveredByCreditInCHFCents),
        date: mapToDotFormattedDate(data.periodEndDate),
      },
    ) as string;
  }

  return null;
});

const shouldShowNextCreditAvailableMessage = computed<boolean>(() => {
  return !!data.nextCreditAvailableDate;
});
const nextCreditAvailableMessage = computed<string>(() => {
  return t('toplisting.modal.immocredit.expanded.newCreditAvailableFromWithAmountCHF', {
    date: mapToDotFormattedDate(data.nextCreditAvailableDate!),
    amountInCHF: formatPriceInCHFCents(data.nextCreditInCHFCents),
  }) as string;
});
const calculateDaysUntilExpiration = computed<number>(() => {
  return calculateDaysUntilTargetDate(data.periodEndDate);
});
const formattedPeriodStartDate = computed<string>(() => {
  return mapToDotFormattedDate(data.periodStartDate);
});
const formattedPeriodEndDate = computed<string>(() => {
  return mapToDotFormattedDate(data.periodEndDate);
});
const calculatedCreditTop = computed<string>(() => {
  return hgNumber(data.maximumTopListingDays ?? 0, route.params.locale as HgSupportedLanguage);
});
const calculatedCreditPremium = computed<string>(() => {
  return hgNumber(data.maximumPremiumListingDays ?? 0, route.params.locale as HgSupportedLanguage);
});

const roundToTwoDecimalPlaces = (positiveNumber: number): number =>
  roundToTwoDecimals(positiveNumber);
const formatPriceInCHFCents = (priceInCHFCents: number): string =>
  formatNumberWithTwoDecimals(priceInCHFCents / 100, locale.value);
</script>

<style lang="scss" module>
@import '@/assets/variables';
@import '@/assets/mixins';
@import '@smg-real-estate/domus/style-tokens-mixins.scss';

.creditAvailableInCHF {
  @include body-2-bold;
}

.collapsible,
.errorMessage {
  @include body-3($color-deep-blue-80);

  & {
    padding: $space-100 $space-100 + $space-25;
    font-size: $space-50 + $space-25;
    border: 1px solid $color-deep-blue-20;
    border-radius: $space-50;
  }
}

.collapsibleHeaderContent {
  @include body-3;

  & {
    width: 100%;
  }
}

.collapsibleHeaderFirstRow {
  display: flex;
  justify-content: space-between;
}

.creditToplistingDays {
  margin-bottom: $space-75;
}

.collapsibleTrigger {
  align-items: start;
}

.collapsibleBodyContent {
  @include body-3;
}

.notificationContainer {
  margin-top: $space-100;
}

.notificationNoIcon {
  display: none;
}

.notificationMessage {
  margin-top: $space-25;
}

.notificationTitle {
  @include body-3-bold;
}

.premiumDaysText {
  margin-left: $space-100 + $space-25;
}

.hint {
  @include caption;
}

.donutChart,
.donutChartLegend {
  box-sizing: border-box;
  display: block; /* Stack vertically by default for mobile */
  width: 100%; /* Full width by default for mobile */
}

.donutChartLegend {
  margin-top: $space-100;
}

@media (min-width: $viewport-md-min) {
  .donutChart,
  .donutChartLegend {
    display: inline-block;
    width: 50%; /* Equal width for desktop */
    margin-top: $space-100;
    vertical-align: top; /* Top alignment */
  }
}
</style>
